import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BackPill } from './BackPill';
import { X, ChevronLeft, ChevronRight, ListChecks } from 'lucide-react';
import { vibrate } from '../../utils/haptics';
import { useNativeBack } from '../../hooks/useHistoryClose';
import {
  GOLD, GOLD_HI, INK, MONTHS, loadAgenda, saveAgenda, monthDays, computeStats, todayKey as getTodayKey, tickSound,
} from './agenda/agendaStore';
import { StatBoxes, Donut, DailyBars, WeeklyBars } from './agenda/AgendaCharts';
import { AgendaGrid } from './agenda/AgendaGrid';
import { AgendaAnalysis, TopHabits } from './agenda/AgendaAnalysis';
import { AgendaForge } from './agenda/AgendaForge';

const SLEEP_STEPS = [0, 4, 5, 6, 7, 8, 9, 10];

const MonthSwitch = ({ y, m, onShift }) => (
  <div className="flex items-center gap-2">
    <button onClick={() => onShift(-1)} data-testid="agenda-month-prev" className="p-1.5 rounded-full active:scale-90"
      style={{ border: '1px solid rgba(212,175,55,0.35)' }}><ChevronLeft size={14} color={GOLD_HI}/></button>
    <span className="font-['Cinzel'] uppercase tracking-[0.2em] text-center" data-testid="agenda-month-label"
      style={{ color: '#fff', fontSize: 12, minWidth: 116 }}>{MONTHS[m]} {y}</span>
    <button onClick={() => onShift(1)} data-testid="agenda-month-next" className="p-1.5 rounded-full active:scale-90"
      style={{ border: '1px solid rgba(212,175,55,0.35)' }}><ChevronRight size={14} color={GOLD_HI}/></button>
  </div>
);

const EmptyState = ({ onForge }) => (
  <div className="flex flex-col items-center text-center px-6 py-10 rounded-2xl" data-testid="agenda-empty"
    style={{ border: '1px dashed rgba(212,175,55,0.35)' }}>
    <ListChecks size={34} color={GOLD_HI}/>
    <p className="mt-3 font-['Cormorant_Garamond'] italic" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 17 }}>
      Forge your daily schedule — every item becomes a checklist row for the whole month.
    </p>
    <button onClick={onForge} data-testid="agenda-empty-forge"
      className="mt-5 px-6 py-2.5 rounded-full font-['Cinzel'] uppercase tracking-[0.22em] active:scale-95"
      style={{ background: `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`, color: '#0a0806', fontSize: 12, fontWeight: 700 }}>
      Forge Agenda
    </button>
  </div>
);

export default function TheAgenda({ onClose }) {
  const [state, setState] = useState(loadAgenda);
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [forge, setForge] = useState(null); // null | 'new' | item
  const today = getTodayKey();
  useNativeBack(!!forge, () => setForge(null));

  const saveTimer = useRef(null);
  useEffect(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveAgenda(state), 150);
  }, [state]);
  useEffect(() => () => { clearTimeout(saveTimer.current); }, []);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => () => saveAgenda(stateRef.current), []);

  const days = useMemo(() => monthDays(ym.y, ym.m), [ym]);
  const stats = useMemo(() => computeStats(state, days), [state, days]);

  const shift = (delta) => setYm(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const onToggle = useCallback((itemId, key) => {
    const on = !stateRef.current.checks[key]?.[itemId];
    tickSound(on);
    vibrate(on ? 14 : 8);
    setState((s) => {
      const day = { ...(s.checks[key] || {}) };
      if (on) day[itemId] = true; else delete day[itemId];
      return { ...s, checks: { ...s.checks, [key]: day } };
    });
  }, []);

  const onCycle = useCallback((kind, key) => {
    const cur = stateRef.current[kind][key] || 0;
    const next = kind === 'mood' ? (cur + 1) % 6 : SLEEP_STEPS[(SLEEP_STEPS.indexOf(cur) + 1) % SLEEP_STEPS.length];
    tickSound(next !== 0);
    vibrate(8);
    setState((s) => ({ ...s, [kind]: { ...s[kind], [key]: next } }));
  }, []);

  const onSaveItem = (it) => {
    vibrate(20);
    tickSound(true);
    setState((s) => (it.id
      ? { ...s, items: s.items.map((x) => (x.id === it.id ? it : x)) }
      : { ...s, items: [...s.items, { ...it, id: `ag_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}` }] }));
    if (it.id) setForge(null);
  };
  const onDeleteItem = (id) => {
    setState((s) => ({ ...s, items: s.items.filter((x) => x.id !== id) }));
    setForge(null);
  };

  return (
    <div className="fixed inset-0 z-[220] flex flex-col" data-testid="agenda-modal" style={{ background: INK }}>
      <div className="flex-shrink-0 flex items-center justify-between px-4 pb-2"
        style={{ paddingTop: 'max(12px, var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))', borderBottom: '1px solid rgba(212,175,55,0.18)' }}>
        <BackPill onClick={onClose} accent="#E8404F" testId="agenda-close"/>
        <span className="font-['Cinzel'] uppercase tracking-[0.34em]" style={{ color: GOLD_HI, fontSize: 15 }}>Agenda</span>
        <span className="w-[84px]"/>
      </div>
      <div className="flex-shrink-0 flex justify-center py-2"><MonthSwitch y={ym.y} m={ym.m} onShift={shift}/></div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pb-10 space-y-3" data-testid="agenda-body">
        {state.items.length === 0 ? <EmptyState onForge={() => setForge('new')}/> : (
          <>
            <div className="flex gap-3 items-center rounded-2xl p-3"
              style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.22)' }}>
              <StatBoxes stats={stats}/>
              <Donut stats={stats}/>
            </div>
            <DailyBars daily={stats.daily} todayKey={today}/>
            <WeeklyBars weekly={stats.weekly}/>
            <AgendaGrid state={state} days={days} todayKey={today} onToggle={onToggle} onCycle={onCycle}
              onEdit={(it) => setForge(it)} onForge={() => setForge('new')}/>
            <p className="text-center font-['Outfit']" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
              Swipe the days sideways · tap an item to edit · tap Mood / Sleep cells to set
            </p>
            <AgendaAnalysis perItem={stats.perItem}/>
            <TopHabits perItem={stats.perItem}/>
          </>
        )}
      </div>

      {forge && (
        <AgendaForge items={state.items} editing={forge === 'new' ? null : forge}
          onSave={onSaveItem} onDelete={onDeleteItem} onClose={() => setForge(null)}/>
      )}
    </div>
  );
}
